import {
  BaseInput,
  BaseInputSchema,
  InputType,
  NumberInput,
  SelectInput,
  SelectOption
} from '@innovactions/frontend-library';
import {Validators} from '@angular/forms';
import {phoneValidator} from '../../validator/phone.validator';
import {EmploymentType} from '../../enum/employment-type';
import {SalaryType} from '../../enum/salary-type';
import {Status} from '../../enum/status';
import {InflowType} from '../../enum/inflow-type';
import {inject} from '@angular/core';
import {CurrentUserService} from '../../service/current-user';
import {Role} from '../../enum/role';

export class ProfileManagementSchema extends BaseInputSchema<any> {

  override getStylingType() {
    return null;
  }

  protected override getOrderedInputKeys(): string[] {
    return [
      'employeeNr',
      'firstname',
      'lastname',
      'initials',
      'birthDate',
      'email',
      'phoneNumber',
      'team',
      'employmentDate',
      'employmentEndDate',
      'employmentType',
      'inflowType',
      'status',
      'contractHours',
      'fte',
      'jobScale',
      'salaryType',
      'highestEducation',
      'linkedInUrl'
    ];
  }

  protected override getEnumValue(value: any) {
    return value;
  }

  override getWrapperStyling(): string {
    return "";
  }

  override getDefaultInputs(): BaseInput[] {
    const currentUserService = inject(CurrentUserService);
    if (!currentUserService.hasAnyRole([Role.MANAGER])) {
      return [
        new BaseInput('employeeNr', "Medewerkernummer", InputType.TEXT, undefined, false, true, [], "disabled:bg-gray-200"),
        new BaseInput('firstname', "Voornaam", InputType.TEXT, undefined, true, true, [], "disabled:bg-gray-200"),
        new BaseInput('lastname', "Achternaam", InputType.TEXT, undefined, true, true, [], "disabled:bg-gray-200"),
        new BaseInput('initials', "Initialen", InputType.TEXT, undefined, false, true, [], "disabled:bg-gray-200"),
        new BaseInput('birthDate', "Geboortedatum", InputType.DATE, undefined, false, true, [], "disabled:bg-gray-200"),
        new BaseInput('email', 'Zakelijk e-mailadres', InputType.TEXT, undefined, true, true, [Validators.email], "disabled:bg-gray-200"),
        new BaseInput('phoneNumber', "Telefoonnummer", InputType.TEXT, undefined, false, true, [phoneValidator()], "disabled:bg-gray-200"),
        new BaseInput('team', "Team / kostenplaats", InputType.TEXT, undefined, false, true, [], "disabled:bg-gray-200"),
        new BaseInput('functionName', "Functie naam", InputType.TEXT, undefined, false, true, [], "disabled:bg-gray-200"),
        new BaseInput('jobScale', "Salaris / CAO schaal", InputType.TEXT, undefined, false, true, [], "disabled:bg-gray-200"),
        //NOTE: Show the managers in the company and select 1
        new BaseInput('employmentDate', "Datum in dienst", InputType.DATE, undefined, true, true, [], "disabled:bg-gray-200"),
        new BaseInput('employmentEndDate', "Datum uit dienst", InputType.DATE, undefined, false, true, [], "disabled:bg-gray-200"),
        new SelectInput('employmentType', 'Type dienstverband', EmploymentType.PERMANENT, true, true, [], "disabled:bg-gray-200", undefined, [
          new SelectOption(1, "Vast", EmploymentType.PERMANENT, false),
          new SelectOption(2, "Tijdelijk", EmploymentType.TEMPORARY, false),
          new SelectOption(3, "Stagiair", EmploymentType.INTERN, false)
        ], true),
        new SelectInput('salaryType', "Type salaris", SalaryType.PAID, true, true, [], "disabled:bg-gray-200", undefined, [
          new SelectOption(1, "Betaald", SalaryType.PAID, false),
          new SelectOption(2, "Onbetaald", SalaryType.UNPAID, false),
          new SelectOption(3, "Vrijwilliger zonder vergoeding", SalaryType.VOLUNTEER_UNPAID, false),
          new SelectOption(4, "Vrijwilliger met vergoeding", SalaryType.VOLUNTEER_PAID, false),
        ], true),
        new BaseInput('contractHours', "Aantal contracturen per week", InputType.NUMBER, undefined, false, true, [], "disabled:bg-gray-200"),
        new NumberInput('fte', "Aantal/hoeveelheid FTE", undefined, true, true, [], "disabled:bg-gray-200", undefined, 0, undefined, 0.1, false),
        new SelectInput('status', "Status", Status.ACTIVE, true, true, [], "disabled:bg-gray-200", undefined, [
          new SelectOption(1, "Actief", Status.ACTIVE, false),
          new SelectOption(2, "Uit dienst", Status.INACTIVE, false),
          new SelectOption(3, "Verlof", Status.LEAVE, false),
        ], true),
        new BaseInput('linkedInUrl', "LinkedIn profiel", InputType.TEXT, undefined, false, false, [], "disabled:bg-gray-200"),
        new BaseInput('highestEducation', "Hoogste opleiding", InputType.TEXT, undefined, true, false, [], "disabled:bg-gray-200"),
        new SelectInput('inflowType', "Type instroom", InflowType.REGULAR, true, false, [], "disabled:bg-gray-200", undefined, [
          new SelectOption(1, "Participatiewet", InflowType.PARTICIPATION, false),
          new SelectOption(2, "Uitkering UWV", InflowType.BENEFIT, false),
          new SelectOption(3, "Niet-uitkeringsgerechtigde (NUG)", InflowType.NON_BENEFIT_RECIPIENT, false),
          new SelectOption(4, "Langdurig werkzoekende", InflowType.LONG_TERM_JOB_SEEKER, false),
          new SelectOption(5, "Doelgroep banenafspraak", InflowType.TARGET_GROUP_JOB_AGREEMENT, false),
          new SelectOption(6, "Beschut werk", InflowType.SHELTERED_WORK, false),
          new SelectOption(7, "Arbeidsbeperking (overig)", InflowType.WORK_DISABILITY, false),
          new SelectOption(8, "BBL-leerling", InflowType.BBL_STUDENT, false),
          new SelectOption(9, "Leer-werktraject", InflowType.LEARN_WORKING_PROCESS, false),
          new SelectOption(10, "Praktijkonderwijs / VSO", InflowType.PRACTICAL_EDUCATION, false),
          new SelectOption(11, "Stagiair (MBO)", InflowType.INTERN_MBO, false),
          new SelectOption(12, "Stagiair (HBO/WO)", InflowType.INTERN_HBO, false),
          new SelectOption(13, "Medewerker sociaal ontwikkelbedrijf", InflowType.EMPLOYEE_SOCIAL_DEVELOPMENT, false),
          new SelectOption(14, "Detachering sociaal ontwikkelbedrijf", InflowType.SECONDMENT_SOCIAL_DEVELOPMENT, false),
          new SelectOption(15, "Re-integratietraject", InflowType.REINTEGRATION, false),
        ], true)
      ];
    }

    return [
      new BaseInput('employeeNr', "Medewerkernummer", InputType.TEXT, undefined, false, false, [], undefined),
      new BaseInput('firstname', "Voornaam", InputType.TEXT, undefined, true, true, [], "disabled:bg-gray-200"),
      new BaseInput('lastname', "Achternaam", InputType.TEXT, undefined, true, true, [], "disabled:bg-gray-200"),
      new BaseInput('initials', "Initialen", InputType.TEXT, undefined, false, false, [], undefined),
      new BaseInput('birthDate', "Geboortedatum", InputType.DATE, undefined, false, false, [], undefined),
      new BaseInput('email', 'Zakelijk e-mailadres', InputType.TEXT, undefined, true, true, [Validators.email], "disabled:bg-gray-200"),
      new BaseInput('phoneNumber', "Telefoonnummer", InputType.TEXT, undefined, false, false, [phoneValidator()], undefined),
      new BaseInput('team', "Team / kostenplaats", InputType.TEXT, undefined, false, false, [], undefined),
      new SelectInput('functionName', "Bedrijfseigen functie voor Prestatie-afspraak", undefined, false, false, [], undefined, undefined, [], true),
      new BaseInput('jobScale', "Salaris / CAO schaal", InputType.TEXT, undefined, false, false, [], undefined),
      //NOTE: Show the managers in the company and select 1
      new BaseInput('employmentDate', "Datum in dienst", InputType.DATE, undefined, true, false, [], undefined),
      new BaseInput('employmentEndDate', "Datum uit dienst", InputType.DATE, undefined, false, false, [], undefined),
      new SelectInput('employmentType', 'Type dienstverband', EmploymentType.PERMANENT, false, false, [], undefined, undefined, [
        new SelectOption(1, "Vast", EmploymentType.PERMANENT, false),
        new SelectOption(2, "Tijdelijk", EmploymentType.TEMPORARY, false),
        new SelectOption(3, "Stagiair", EmploymentType.INTERN, false)
      ], true),
      new SelectInput('salaryType', "Type salaris", SalaryType.PAID, false, false, [], undefined, undefined, [
        new SelectOption(1, "Betaald", SalaryType.PAID, false),
        new SelectOption(2, "Onbetaald", SalaryType.UNPAID, false),
        new SelectOption(3, "Vrijwilliger zonder vergoeding", SalaryType.VOLUNTEER_UNPAID, false),
        new SelectOption(4, "Vrijwilliger met vergoeding", SalaryType.VOLUNTEER_PAID, false),
      ], true),
      new BaseInput('contractHours', "Aantal contracturen per week", InputType.NUMBER, undefined, false, false, [], undefined),
      new NumberInput('fte', "Aantal/hoeveelheid FTE", undefined, false, false, [], undefined, undefined, 0, undefined, 0.1, false),
      new SelectInput('status', "Status", Status.ACTIVE, false, false, [], undefined, undefined, [
        new SelectOption(1, "Actief", Status.ACTIVE, false),
        new SelectOption(2, "Uit dienst", Status.INACTIVE, false),
        new SelectOption(3, "Verlof", Status.LEAVE, false),
      ], true),
      new BaseInput('linkedInUrl', "LinkedIn profiel", InputType.TEXT, undefined, false, false, [], undefined),
      new BaseInput('highestEducation', "Hoogste opleiding", InputType.TEXT, undefined, false, false, [], undefined),
      new SelectInput('inflowType', "Type instroom", InflowType.REGULAR, false, false, [], undefined, undefined, [
        new SelectOption(1, "Participatiewet", InflowType.PARTICIPATION, false),
        new SelectOption(2, "Uitkering UWV", InflowType.BENEFIT, false),
        new SelectOption(3, "Niet-uitkeringsgerechtigde (NUG)", InflowType.NON_BENEFIT_RECIPIENT, false),
        new SelectOption(4, "Langdurig werkzoekende", InflowType.LONG_TERM_JOB_SEEKER, false),
        new SelectOption(5, "Doelgroep banenafspraak", InflowType.TARGET_GROUP_JOB_AGREEMENT, false),
        new SelectOption(6, "Beschut werk", InflowType.SHELTERED_WORK, false),
        new SelectOption(7, "Arbeidsbeperking (overig)", InflowType.WORK_DISABILITY, false),
        new SelectOption(8, "BBL-leerling", InflowType.BBL_STUDENT, false),
        new SelectOption(9, "Leer-werktraject", InflowType.LEARN_WORKING_PROCESS, false),
        new SelectOption(10, "Praktijkonderwijs / VSO", InflowType.PRACTICAL_EDUCATION, false),
        new SelectOption(11, "Stagiair (MBO)", InflowType.INTERN_MBO, false),
        new SelectOption(12, "Stagiair (HBO/WO)", InflowType.INTERN_HBO, false),
        new SelectOption(13, "Medewerker sociaal ontwikkelbedrijf", InflowType.EMPLOYEE_SOCIAL_DEVELOPMENT, false),
        new SelectOption(14, "Detachering sociaal ontwikkelbedrijf", InflowType.SECONDMENT_SOCIAL_DEVELOPMENT, false),
        new SelectOption(15, "Nieuwkomer", InflowType.NEWCOMER, false),
        new SelectOption(16, "Overige SROI-doelgroep", InflowType.SROI_TARGET_AUDIENCE, false),
        new SelectOption(17, "Regulier", InflowType.REGULAR, false),
        new SelectOption(19, "Proefplaatsing", InflowType.TRIAL_PLACEMENT, false),
        new SelectOption(20, "Werkervaringplaats", InflowType.WORK_EXPERIENCE_PLACEMENT, false),
        new SelectOption(21, "Zij-instroom/omscholer", InflowType.CAREER_SWITCH, false),
      ], true)
    ];
  }
}
